import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import * as XLSX from 'xlsx'
import { C, R, F, S, btnPrimary, btnSecondary, btnDanger, card, input, thStyle, kpiCard } from '../lib/designTokens'

const fmt  = n => new Intl.NumberFormat('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 }).format(n||0)
const fmt0 = n => new Intl.NumberFormat('en-US', { maximumFractionDigits:0 }).format(n||0)
const fmtD = s => s ? new Date(s).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'

const ITEM_TYPES = [
  { value:'original_contract', label:'Original Contract',  color: C.green,  sign: +1 },
  { value:'variation',         label:'Variation Order',    color: C.blue,   sign: +1 },
  { value:'claim',             label:'Claim',              color: C.amber,  sign: +1 },
  { value:'prolongation',      label:'Prolongation Cost',  color:'#7C3AED', sign: +1 },
  { value:'deduction',         label:'Deduction',          color: C.red,    sign: -1 },
  { value:'liquidated_damages',label:'Liquidated Damages', color: C.red,    sign: -1 },
  { value:'retention_release', label:'Retention Release',  color: C.green,  sign: +1 },
  { value:'other',             label:'Other',              color: C.sub,    sign: +1 },
]
const STATUSES = ['pending','agreed','certified','disputed','rejected']
const STATUS_STYLE = {
  pending:   { bg:'#FEF3C7', color: C.amber, border:'#FDE68A' },
  agreed:    { bg:'#DBEAFE', color:'#1D4ED8', border:'#BFDBFE' },
  certified: { bg:'#DCFCE7', color: C.green, border:'#BBF7D0' },
  disputed:  { bg:'#FEF2F2', color: C.red,   border:'#FECACA' },
  rejected:  { bg:'#F3F4F6', color: C.sub,   border: C.border },
}

const emptyForm = { item_type:'variation', ref:'', description:'', claimed_amount:'', certified_amount:'', status:'pending', approved_date:'', fidic_clause:'', linked_ref:'', notes:'' }

export default function FinalAccount({ projectId: initProjectId }) {
  const [projects,       setProjects]      = useState([])
  const [projectId,      setProjectId]     = useState(null)
  const [projectName,    setProjectName]   = useState('')
  const { can } = useProjectAuth(projectId)
  const [contractValue,  setContractValue] = useState(0)
  const [items,          setItems]         = useState([])
  const [loading,        setLoading]       = useState(false)
  const [showModal,      setShowModal]     = useState(false)
  const [editItem,       setEditItem]      = useState(null)
  const [form,           setForm]          = useState(emptyForm)
  const [filterType,     setFilterType]    = useState('all')
  const [filterStatus,   setFilterStatus]  = useState('all')
  const [saving,         setSaving]        = useState(false)
  const [selectedIds,    setSelectedIds]   = useState(new Set())
  const [syncing,        setSyncing]       = useState(false)
  const [syncResult,     setSyncResult]    = useState(null)
  const [uploading,      setUploading]     = useState(false)
  const [uploadResult,   setUploadResult]  = useState(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    supabase.from('projects').select('id,name,contract_value').order('name').then(({data})=>setProjects(data||[]))
  }, [])
  useEffect(() => {
    if (initProjectId) {
      setProjectId(initProjectId)
      supabase.from('projects').select('name,contract_value').eq('id',initProjectId).single().then(({data})=>{ setProjectName(data?.name||''); setContractValue(data?.contract_value||0) })
    }
  }, [initProjectId])
  useEffect(() => { if (projectId) fetchItems() }, [projectId])

  async function fetchItems() {
    setLoading(true)
    const { data } = await supabase.from('final_account_items').select('*').eq('project_id',projectId).order('created_at',{ascending:true})
    setItems(data||[])
    setLoading(false)
  }

  // ── Sync from Change Register + EOT
  async function syncFromModules() {
    if (!projectId) return
    setSyncing(true); setSyncResult(null)
    let synced = 0, skipped = 0
    try {
      // 1. Pull approved VOs from technical_changes + change_cost_impact
      const { data: changes } = await supabase.from('technical_changes').select('id,change_ref,change_description,status').eq('project_id',projectId).eq('status','approved')
      for (const ch of (changes||[])) {
        // check if already synced
        const { data: existing } = await supabase.from('final_account_items').select('id').eq('project_id',projectId).eq('linked_ref',ch.change_ref).eq('item_type','variation').maybeSingle()
        if (existing) { skipped++; continue }
        // get net impact
        const { data: impacts } = await supabase.from('change_cost_impact').select('added_amount,omitted_amount').eq('change_id',ch.id)
        const net = (impacts||[]).reduce((s,i)=>s+(i.added_amount||0)-(i.omitted_amount||0),0)
        await supabase.from('final_account_items').insert({
          project_id: projectId, item_type:'variation',
          ref: ch.change_ref, description: ch.change_description,
          claimed_amount: net, certified_amount: net,
          status:'certified', source:'change_register', linked_ref: ch.change_ref,
          fidic_clause:'13.1',
        })
        synced++
      }
      // 2. Pull approved EOTs prolongation cost
      const { data: eots } = await supabase.from('eot_register').select('*').eq('project_id',projectId).in('status',['approved','partially_approved'])
      for (const e of (eots||[])) {
        if (!e.prolongation_cost_approved && !e.prolongation_cost) continue
        const { data: existing } = await supabase.from('final_account_items').select('id').eq('project_id',projectId).eq('linked_ref',e.eot_ref).eq('item_type','prolongation').maybeSingle()
        if (existing) { skipped++; continue }
        const cost = e.prolongation_cost_approved || e.prolongation_cost || 0
        if (!cost) continue
        await supabase.from('final_account_items').insert({
          project_id: projectId, item_type:'prolongation',
          ref: e.eot_ref, description: `Prolongation cost — ${e.title}`,
          claimed_amount: e.prolongation_cost||cost, certified_amount: cost,
          status: e.status==='approved'?'certified':'agreed',
          source:'eot_register', linked_ref: e.eot_ref,
          fidic_clause: e.fidic_clause?.split('—')[0].trim()||'8.4',
        })
        synced++
      }
      setSyncResult({ synced, skipped })
      await fetchItems()
    } catch(err) { setSyncResult({ synced:0, skipped:0, error: err.message }) }
    setSyncing(false)
  }

  async function saveItem() {
    if (!form.description||!projectId) return
    setSaving(true)
    const payload = {
      project_id: projectId,
      item_type: form.item_type, ref: form.ref||null, description: form.description,
      claimed_amount: parseFloat(form.claimed_amount)||0,
      certified_amount: parseFloat(form.certified_amount)||0,
      status: form.status, approved_date: form.approved_date||null,
      fidic_clause: form.fidic_clause||null, linked_ref: form.linked_ref||null,
      notes: form.notes||null, source:'manual',
    }
    if (editItem) { await supabase.from('final_account_items').update(payload).eq('id',editItem.id) }
    else          { await supabase.from('final_account_items').insert(payload) }
    setSaving(false); setShowModal(false); setEditItem(null); setForm(emptyForm); fetchItems()
  }

  async function deleteItem(id) {
    if (!window.confirm('Delete this item?')) return
    await supabase.from('final_account_items').delete().eq('id',id); fetchItems()
  }
  async function bulkDelete() {
    if (!selectedIds.size) return
    if (!window.confirm(`Delete ${selectedIds.size} item(s)?`)) return
    await supabase.from('final_account_items').delete().in('id',[...selectedIds])
    setSelectedIds(new Set()); fetchItems()
  }
  function toggleSelect(id,e) { e.stopPropagation(); setSelectedIds(prev=>{ const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n }) }
  function toggleSelectAll()  { setSelectedIds(selectedIds.size===filtered.length?new Set():new Set(filtered.map(i=>i.id))) }
  function openEdit(item) { setEditItem(item); setForm({ ...emptyForm, ...item, claimed_amount:item.claimed_amount||'', certified_amount:item.certified_amount||'', approved_date:item.approved_date||'', fidic_clause:item.fidic_clause||'', linked_ref:item.linked_ref||'' }); setShowModal(true) }


  function exportPDF() {
    const fmt0 = n => new Intl.NumberFormat('en-US', { maximumFractionDigits:0 }).format(n||0)
    const fmtD = s => s ? new Date(s).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'

    const typeLabel = { original_contract:'Original Contract', variation:'Variation Order', claim:'Claim', prolongation:'Prolongation Cost', deduction:'Deduction', liquidated_damages:'Liquidated Damages', retention_release:'Retention Release', other:'Other' }
    const typeColor = { original_contract:'#1A7A4A', variation:'#1B3A6B', claim:'#D97706', prolongation:'#7C3AED', deduction:'#DC2626', liquidated_damages:'#DC2626', retention_release:'#1A7A4A', other:'#6B7280' }

    // group by type for summary
    const byType = {}
    items.forEach(i => { byType[i.item_type] = byType[i.item_type] || []; byType[i.item_type].push(i) })
    const certTotal = items.reduce((s,i)=>{ const t=ITEM_TYPES.find(t=>t.value===i.item_type); return s+(i.certified_amount||0)*(t?.sign||1) },0)
    const clmTotal  = items.reduce((s,i)=>{ const t=ITEM_TYPES.find(t=>t.value===i.item_type); return s+(i.claimed_amount||0)*(t?.sign||1) },0)

    const rows = items.map((item,idx) => {
      const t = ITEM_TYPES.find(t=>t.value===item.item_type)
      const sign = t?.sign||1
      const claimed = (item.claimed_amount||0)*sign
      const certified = (item.certified_amount||0)*sign
      const diff = certified - claimed
      const statusColors = { pending:'#D97706', agreed:'#1D4ED8', certified:'#1A7A4A', disputed:'#DC2626', rejected:'#6B7280' }
      return `
        <tr style="background:${idx%2===0?'#fff':'#F9FAFB'}; border-bottom:1px solid #E5E7EB;">
          <td style="padding:8px 10px; font-family:monospace; font-size:11px; color:#6B7280;">${item.ref||'—'}</td>
          <td style="padding:8px 10px;">
            <span style="display:inline-block; padding:2px 8px; border-radius:4px; font-size:10px; font-weight:700; background:${(typeColor[item.item_type]||'#6B7280')}18; color:${typeColor[item.item_type]||'#6B7280'};">${typeLabel[item.item_type]||item.item_type}</span>
          </td>
          <td style="padding:8px 10px; font-size:11px; max-width:260px;">${item.description}</td>
          <td style="padding:8px 10px; font-size:11px; color:#6B7280; font-family:monospace;">${item.fidic_clause||'—'}</td>
          <td style="padding:8px 10px; text-align:right; font-family:monospace; font-size:11px; color:${claimed<0?'#DC2626':'#0F1017'};">${claimed<0?'('+fmt0(Math.abs(claimed))+')':fmt0(claimed)}</td>
          <td style="padding:8px 10px; text-align:right; font-family:monospace; font-size:11px; font-weight:700; color:${certified<0?'#DC2626':'#1A7A4A'};">${certified<0?'('+fmt0(Math.abs(certified))+')':fmt0(certified)}</td>
          <td style="padding:8px 10px; text-align:right; font-family:monospace; font-size:11px; color:${diff>0?'#1A7A4A':diff<0?'#DC2626':'#6B7280'};">${diff===0?'—':diff>0?'+'+fmt0(diff):'('+fmt0(Math.abs(diff))+')'}</td>
          <td style="padding:8px 10px; text-align:center;">
            <span style="padding:2px 8px; border-radius:10px; font-size:10px; font-weight:700; color:${statusColors[item.status]||'#6B7280'}; background:${statusColors[item.status]||'#6B7280'}18;">${item.status?.charAt(0).toUpperCase()+item.status?.slice(1)}</span>
          </td>
        </tr>`
    }).join('')

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Final Account Statement — ${projectName}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: Arial, sans-serif; font-size:12px; color:#0F1017; background:#fff; }
  @page { size: A4 landscape; margin: 15mm 12mm; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style>
</head>
<body>
  <!-- Header -->
  <div style="background:#1B3A6B; color:#fff; padding:16px 20px; margin-bottom:16px; border-radius:6px;">
    <div style="display:flex; justify-content:space-between; align-items:flex-start;">
      <div>
        <div style="font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.1em; color:rgba(255,255,255,.6); margin-bottom:4px;">Final Account Statement</div>
        <div style="font-size:20px; font-weight:700;">${projectName}</div>
        <div style="font-size:11px; color:rgba(255,255,255,.7); margin-top:4px;">Prepared: ${new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'})}</div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:9px; color:rgba(255,255,255,.6); text-transform:uppercase; letter-spacing:.07em;">Certified Final Value</div>
        <div style="font-size:28px; font-weight:700; color:#FDE68A; font-family:monospace;">SAR ${fmt0(certTotal)}</div>
      </div>
    </div>
  </div>

  <!-- Summary Bar -->
  <div style="display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-bottom:16px;">
    ${[
      { label:'Original Contract', val: fmt0(contractValue||0), color:'#1A7A4A' },
      { label:'Variations (Net Certified)', val: fmt0(items.filter(i=>i.item_type==='variation').reduce((s,i)=>s+(i.certified_amount||0),0)), color:'#1B3A6B' },
      { label:'Prolongation (Certified)', val: fmt0(items.filter(i=>i.item_type==='prolongation').reduce((s,i)=>s+(i.certified_amount||0),0)), color:'#7C3AED' },
      { label:'Deductions', val: '('+fmt0(items.filter(i=>['deduction','liquidated_damages'].includes(i.item_type)).reduce((s,i)=>s+(i.certified_amount||0),0))+')', color:'#DC2626' },
    ].map(k=>`
      <div style="background:#F9FAFB; border:1px solid #E5E7EB; border-radius:6px; padding:12px 14px;">
        <div style="font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; color:#6B7280; margin-bottom:6px;">${k.label}</div>
        <div style="font-size:16px; font-weight:700; color:${k.color}; font-family:monospace;">${k.val}</div>
      </div>`).join('')}
  </div>

  <!-- Table -->
  <table style="width:100%; border-collapse:collapse; font-size:11px;">
    <thead>
      <tr style="background:#1B3A6B; color:#fff;">
        <th style="padding:8px 10px; text-align:left; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; width:70px;">Ref</th>
        <th style="padding:8px 10px; text-align:left; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; width:130px;">Type</th>
        <th style="padding:8px 10px; text-align:left; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.07em;">Description</th>
        <th style="padding:8px 10px; text-align:left; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; width:55px;">FIDIC</th>
        <th style="padding:8px 10px; text-align:right; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; width:120px;">Claimed (SAR)</th>
        <th style="padding:8px 10px; text-align:right; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; width:120px;">Certified (SAR)</th>
        <th style="padding:8px 10px; text-align:right; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; width:90px;">Diff</th>
        <th style="padding:8px 10px; text-align:center; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; width:80px;">Status</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
    <tfoot>
      <tr style="background:#F3F4F6; border-top:2px solid #1B3A6B;">
        <td colspan="4" style="padding:10px; font-weight:700; font-size:12px;">TOTAL (${items.length} items)</td>
        <td style="padding:10px; text-align:right; font-family:monospace; font-weight:700; font-size:12px;">${fmt0(clmTotal)}</td>
        <td style="padding:10px; text-align:right; font-family:monospace; font-weight:700; font-size:12px; color:#1A7A4A;">${fmt0(certTotal)}</td>
        <td style="padding:10px; text-align:right; font-family:monospace; font-weight:700; font-size:12px; color:${certTotal-clmTotal>=0?'#1A7A4A':'#DC2626'};">${certTotal-clmTotal>=0?'+'+fmt0(certTotal-clmTotal):'('+fmt0(Math.abs(certTotal-clmTotal)+')')}</td>
        <td></td>
      </tr>
    </tfoot>
  </table>

  <!-- Footer -->
  <div style="margin-top:20px; padding-top:12px; border-top:1px solid #E5E7EB; display:flex; justify-content:space-between; font-size:9px; color:#9CA3AF;">
    <span>CostSpace Pro — Final Account Statement</span>
    <span>This document is generated automatically from project data. Subject to Engineer certification.</span>
    <span>Page 1 of 1</span>
  </div>
</body>
</html>`

    const win = window.open('', '_blank', 'width=1200,height=800')
    win.document.write(html)
    win.document.close()
    win.onload = () => { win.focus(); win.print() }
  }

  function downloadTemplate() {
    const wb = XLSX.utils.book_new()
    const H = ['item_type','ref','description','claimed_amount','certified_amount','status','approved_date','fidic_clause','linked_ref','notes']
    const D = [
      ['original_contract','OC-001','Original Contract Sum — Jeddah Oceanarium Project',1934920684,1934920684,'certified','2023-01-15','','','Lump sum contract. Signed 15 Jan 2023.'],
      ['variation','VO-001','Additional piling works — unforeseen soil conditions (Cl.4.12)',4200000,4200000,'certified','2024-04-02','4.12','VO-001','Approved variation. Cost impact SAR 4.2M.'],
      ['variation','VO-002','Omission of temporary access road',-1850000,-1850000,'certified','2024-04-10','13.1','VO-002','Omission agreed. Net reduction SAR 1.85M.'],
      ['variation','VO-003','Aquarium main tank structural upgrade',8750000,6500000,'agreed','','13.3','VO-003','Agreed in principle. Engineer certification pending.'],
      ['variation','VO-004','Upgrade MEP spec in VIP areas',3100000,0,'pending','','13.2','VO-004','Submitted. Awaiting Engineer assessment.'],
      ['variation','VO-005','Additional facade cladding — east elevation',2600000,2600000,'certified','2024-05-25','13.1','VO-005','Approved and certified.'],
      ['claim','CLAIM-001','Disruption claim — concurrent delays Q1 2024',2200000,0,'disputed','','20.1','','Contractor claim under Cl.20.1. Engineer assessing.'],
      ['prolongation','EOT-001','Prolongation cost — delayed drawings (35 days)',1850000,1600000,'certified','2024-06-15','1.9','EOT-001','35-day EOT approved. Prolongation SAR 1.6M certified.'],
      ['prolongation','EOT-002','Prolongation cost — unforeseen ground conditions (28 days)',980000,980000,'certified','2024-07-01','4.12','EOT-002','Full prolongation approved.'],
      ['deduction','DED-001','Defects rectification — provisional deduction',-350000,-350000,'certified','','','','Defect in pile cap waterproofing. Contractor to rectify or deduct.'],
      ['retention_release','RET-001','Retention release — practical completion (50% of 5%)',4837302,4837302,'pending','','','','Retention on original contract + VOs. Release at PC.'],
    ]
    const ws = XLSX.utils.aoa_to_sheet([H,...D])
    ws['!cols'] = H.map((_,i)=>({ wch: i===2?50:i===0?20:18 }))
    XLSX.utils.book_append_sheet(wb, ws, 'Final_Account')
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Field','Valid Values / Notes'],
      ['item_type','original_contract | variation | claim | prolongation | deduction | liquidated_damages | retention_release | other'],
      ['status','pending | agreed | certified | disputed | rejected'],
      ['claimed_amount','Use negative for omissions and deductions'],
      ['TIP','Use Sync button in app to auto-import approved VOs from Change Register and EOTs from EOT Register'],
    ]), 'Valid_Values')
    XLSX.writeFile(wb, 'FinalAccount_Template.xlsx')
  }

  async function handleUpload(e) {
    const file = e.target.files[0]; if (!file||!projectId) return
    setUploading(true); setUploadResult(null)
    try {
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf)
      const ws = wb.Sheets['Final_Account']
      const rows = ws ? XLSX.utils.sheet_to_json(ws,{defval:''}) : []
      let imported=0, skipped=0; const errors=[]
      for (const row of rows) {
        const desc = (row['description']||'').toString().trim()
        if (!desc) { skipped++; continue }
        const { error } = await supabase.from('final_account_items').insert({
          project_id: projectId,
          item_type: row['item_type']?.toString().trim()||'other',
          ref: row['ref']?.toString().trim()||null, description: desc,
          claimed_amount: parseFloat(row['claimed_amount'])||0,
          certified_amount: parseFloat(row['certified_amount'])||0,
          status: row['status']?.toString().trim()||'pending',
          approved_date: row['approved_date']?.toString().trim()||null,
          fidic_clause: row['fidic_clause']?.toString().trim()||null,
          linked_ref: row['linked_ref']?.toString().trim()||null,
          notes: row['notes']?.toString().trim()||null,
          source: 'upload',
        })
        if (error) { errors.push(`${row['ref']||desc.slice(0,25)}: ${error.message}`); skipped++ } else imported++
      }
      setUploadResult({imported,skipped,errors}); await fetchItems()
    } catch(err) { setUploadResult({imported:0,skipped:0,errors:[err.message]}) }
    setUploading(false); if (fileInputRef.current) fileInputRef.current.value=''
  }

  const filtered = items.filter(i => {
    if (filterType!=='all'   && i.item_type!==filterType)  return false
    if (filterStatus!=='all' && i.status!==filterStatus)   return false
    return true
  })

  // ── Financial summary
  const certifiedTotal  = items.reduce((s,i)=>{ const t=ITEM_TYPES.find(t=>t.value===i.item_type); return s+(i.certified_amount||0)*(t?.sign||1) },0)
  const claimedTotal    = items.reduce((s,i)=>{ const t=ITEM_TYPES.find(t=>t.value===i.item_type); return s+(i.claimed_amount||0)*(t?.sign||1) },0)
  const disputedAmt     = items.filter(i=>i.status==='disputed').reduce((s,i)=>s+(i.claimed_amount||0),0)
  const pendingAmt      = items.filter(i=>i.status==='pending').reduce((s,i)=>s+(i.claimed_amount||0),0)
  const variationNet    = items.filter(i=>i.item_type==='variation').reduce((s,i)=>s+(i.certified_amount||0),0)
  const claimsNet       = items.filter(i=>i.item_type==='claim').reduce((s,i)=>s+(i.certified_amount||0),0)
  const deductionsNet   = items.filter(i=>i.item_type==='deduction'||i.item_type==='liquidated_damages').reduce((s,i)=>s+(i.certified_amount||0),0)

  const badge = st => { const s=STATUS_STYLE[st]||STATUS_STYLE.pending; return { display:'inline-flex', alignItems:'center', padding:'2px 10px', borderRadius: R.badge, fontSize: F.caption, fontWeight:600, background:s.bg, color:s.color, border:`1px solid ${s.border}` } }
  const pg = { padding: S.pagePad, minHeight:'100vh', background: C.pageBg, color: C.ink, fontFamily:'Inter,sans-serif', fontSize: F.body }

  return (
    <div style={pg}>
      {/* ── Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize: F.heading, fontWeight:700, color: C.ink, margin:0 }}>Final Account</h1>
          <p style={{ fontSize: F.caption, color: C.sub, marginTop:3 }}>FIDIC Final Account Statement — Claimed vs Certified</p>
          {projectName && <p style={{ fontSize: F.body, color: C.sub, marginTop:2 }}>{projectName}</p>}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          {!initProjectId && (
            <select value={projectId||''} onChange={e=>{ setProjectId(e.target.value); supabase.from('projects').select('name,contract_value').eq('id',e.target.value).single().then(({data})=>{ setProjectName(data?.name||''); setContractValue(data?.contract_value||0) }) }} style={{ ...input, width:'auto' }}>
              <option value="">Select project…</option>
              {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          {/* Sync button */}
          <button onClick={syncFromModules} disabled={syncing||!projectId}
            style={{ ...btnSecondary, fontSize: F.body, borderColor: C.green, color: C.green, opacity:syncing||!projectId?0.5:1 }}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M13 2v4h-4"/><path d="M3 14v-4h4"/><path d="M13 6A6 6 0 003.5 5"/><path d="M3 10a6 6 0 009.5 1"/></svg>
            {syncing?'Syncing…':'Sync from Modules'}
          </button>
          <button onClick={exportPDF} disabled={!projectId||items.length===0}
            style={{ ...btnSecondary, fontSize: F.body, borderColor: C.red, color: C.red, opacity:!projectId||items.length===0?0.4:1 }}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><rect x="2" y="1" width="12" height="14" rx="1"/><polyline points="5,8 8,11 11,8"/><line x1="8" y1="4" x2="8" y2="11"/></svg>
            Export PDF
          </button>
          <button onClick={downloadTemplate} style={{ ...btnSecondary, fontSize: F.body }}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2"/><polyline points="5,7 8,10 11,7"/><line x1="8" y1="2" x2="8" y2="10"/></svg>
            Template
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display:'none' }} onChange={handleUpload}/>
          <button onClick={()=>fileInputRef.current?.click()} disabled={uploading||!projectId} style={{ ...btnSecondary, fontSize: F.body, borderColor: C.blue, color: C.blue, opacity:uploading||!projectId?0.5:1 }}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2"/><polyline points="5,6 8,3 11,6"/><line x1="8" y1="3" x2="8" y2="11"/></svg>
            {uploading?'Uploading…':'Upload Excel'}
          </button>
          {can('FinalAccount.Create') && (
            <button onClick={()=>{ setEditItem(null); setForm(emptyForm); setShowModal(true) }} style={{ ...btnPrimary, fontSize: F.body }}>+ Add Item</button>
          )}
        </div>
      </div>

      {/* ── Sync result */}
      {syncResult && (
        <div style={{ marginBottom:12, padding:'10px 16px', borderRadius: R.card, background: syncResult.error?'#FEF2F2':'#F0FDF4', border:`1px solid ${syncResult.error?C.redLine:C.greenLine}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize: F.body, color: syncResult.error?C.red:C.green, fontWeight:600 }}>
            {syncResult.error ? `Sync error: ${syncResult.error}` : `✓ Synced ${syncResult.synced} item${syncResult.synced!==1?'s':''} from modules · ${syncResult.skipped} already existed`}
          </span>
          <button onClick={()=>setSyncResult(null)} style={{ background:'none', border:'none', cursor:'pointer', color: C.sub, fontSize:14 }}>✕</button>
        </div>
      )}

      {/* ── Upload result */}
      {uploadResult && (
        <div style={{ marginBottom:12, padding:'10px 16px', borderRadius: R.card, background:uploadResult.errors.length?'#FEF3C7':'#F0FDF4', border:`1px solid ${uploadResult.errors.length?'#FDE68A':'#BBF7D0'}` }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
            <span style={{ fontWeight:600, fontSize: F.body, color:uploadResult.errors.length?C.amber:C.green }}>{uploadResult.errors.length===0?'✓ Upload successful':'⚠ Upload with issues'}</span>
            <button onClick={()=>setUploadResult(null)} style={{ background:'none', border:'none', cursor:'pointer', color: C.sub, fontSize:14 }}>✕</button>
          </div>
          <p style={{ fontSize: F.caption, color: C.ink2, margin:0 }}>Imported: <b>{uploadResult.imported}</b> · Skipped: <b>{uploadResult.skipped}</b></p>
          {uploadResult.errors.map((e,i)=><p key={i} style={{ fontSize: F.caption, color: C.amber, margin:'2px 0 0' }}>• {e}</p>)}
        </div>
      )}

      {/* ── Final Account Summary Bar */}
      <div style={{ ...card, marginBottom:20, background: C.blue, border:'none', padding:'16px 20px' }}>
        <p style={{ fontSize: F.caption, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'rgba(255,255,255,.6)', margin:'0 0 12px' }}>Final Account Statement — Jeddah Oceanarium</p>
        <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr auto 1fr auto 1fr', gap:8, alignItems:'center' }}>
          {[
            { label:'Original Contract', val: contractValue||0, color:'#fff' },
            null,
            { label:'Variations (Net)', val: variationNet, color: variationNet>=0?'#6EE7B7':'#FCA5A5', signed:true },
            null,
            { label:'Claims Certified', val: claimsNet, color: claimsNet>0?'#FDE68A':'#6EE7B7', signed:true },
            null,
            { label:'Certified Final Value', val: certifiedTotal, color:'#FDE68A', big:true },
          ].map((k,i) => k===null
            ? <div key={i} style={{ textAlign:'center', color:'rgba(255,255,255,.4)', fontSize:20, fontWeight:300 }}>+</div>
            : <div key={i} style={{ background:'rgba(255,255,255,.08)', borderRadius: R.input, padding:'10px 14px' }}>
                <p style={{ fontSize: F.caption, color:'rgba(255,255,255,.6)', margin:'0 0 4px', whiteSpace:'nowrap' }}>{k.label}</p>
                <p style={{ fontSize: k.big ? F.kpi : F.bodyLg, fontWeight:700, color: k.color, margin:0, fontFamily:'JetBrains Mono,monospace', lineHeight:1 }}>
                  {k.signed && k.val > 0 ? '+' : ''}{fmt0(Math.abs(k.val))}
                </p>
              </div>
          )}
        </div>
        {deductionsNet > 0 && (
          <div style={{ marginTop:10, padding:'8px 14px', borderRadius: R.input, background:'rgba(220,38,38,.15)', border:'1px solid rgba(220,38,38,.3)' }}>
            <span style={{ fontSize: F.caption, color:'#FCA5A5' }}>Total Deductions: <b>({fmt0(deductionsNet)})</b> — Net after deductions: <b>SAR {fmt0(certifiedTotal - deductionsNet)}</b></span>
          </div>
        )}
      </div>

      {/* ── KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
        {[
          { label:'Total Items',       val: items.length,             color: C.ink },
          { label:'Claimed Total',     val:`SAR ${fmt0(claimedTotal)}`,color: C.amber, mono:true },
          { label:'Disputed Amount',   val:`SAR ${fmt0(disputedAmt)}`, color: C.red,   mono:true },
          { label:'Pending Assessment',val:`SAR ${fmt0(pendingAmt)}`,  color: C.sub,   mono:true },
        ].map(k=>(
          <div key={k.label} style={{ ...kpiCard }}>
            <p style={{ fontSize: F.label, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color: C.sub, margin:'0 0 6px' }}>{k.label}</p>
            <p style={{ fontSize: F.kpi, fontWeight:700, color: k.color, margin:0, fontFamily:k.mono?'JetBrains Mono,monospace':'inherit', lineHeight:1.1 }}>{k.val}</p>
          </div>
        ))}
      </div>

      {/* ── Bulk bar */}
      {selectedIds.size>0 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, padding:'10px 16px', borderRadius: R.card, background:'#FEF2F2', border:`1px solid ${C.redLine}` }}>
          <span style={{ fontWeight:600, fontSize: F.body, color: C.red }}>{selectedIds.size} item{selectedIds.size>1?'s':''} selected</span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={()=>setSelectedIds(new Set())} style={{ ...btnSecondary, padding:'5px 12px', fontSize: F.caption }}>Cancel</button>
            {can('FinalAccount.Delete') && (
              <button onClick={bulkDelete} style={{ ...btnDanger, padding:'5px 12px', fontSize: F.caption }}>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><polyline points="3,4 13,4"/><path d="M5 4V2h6v2"/><path d="M4 4l1 10h6l1-10"/></svg>
                Delete {selectedIds.size} selected
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Filters */}
      <div style={{ display:'flex', gap:8, marginBottom:12 }}>
        <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{ ...input, width:'auto', fontSize: F.caption }}>
          <option value="all">All Types</option>
          {ITEM_TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{ ...input, width:'auto', fontSize: F.caption }}>
          <option value="all">All Statuses</option>
          {STATUSES.map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
        </select>
        <span style={{ marginLeft:'auto', fontSize: F.caption, color: C.sub, alignSelf:'center' }}>{filtered.length} item{filtered.length!==1?'s':''}</span>
      </div>

      {/* ── Table */}
      <div style={{ ...card, padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:48, textAlign:'center', color: C.sub }}>Loading…</div> : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize: F.body }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width:36, textAlign:'center' }}>
                    <input type="checkbox" checked={filtered.length>0&&selectedIds.size===filtered.length} onChange={toggleSelectAll} style={{ cursor:'pointer', accentColor: C.blue }}/>
                  </th>
                  <th style={{ ...thStyle, width:80 }}>Ref</th>
                  <th style={{ ...thStyle, width:120 }}>Type</th>
                  <th style={{ ...thStyle }}>Description</th>
                  <th style={{ ...thStyle, width:60 }}>FIDIC</th>
                  <th style={{ ...thStyle, width:140, textAlign:'right' }}>Claimed (SAR)</th>
                  <th style={{ ...thStyle, width:140, textAlign:'right' }}>Certified (SAR)</th>
                  <th style={{ ...thStyle, width:80, textAlign:'right' }}>Diff</th>
                  <th style={{ ...thStyle, width:100, textAlign:'center' }}>Status</th>
                  <th style={{ ...thStyle, width:85 }}>Source</th>
                  <th style={{ ...thStyle, width:56 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length===0 ? (
                  <tr><td colSpan={11} style={{ padding:40, textAlign:'center', color: C.sub }}>No items yet — use "Sync from Modules" or upload template</td></tr>
                ) : filtered.map((item,idx)=>{
                  const typeInfo = ITEM_TYPES.find(t=>t.value===item.item_type)
                  const sign = typeInfo?.sign||1
                  const claimed = (item.claimed_amount||0)*sign
                  const certified = (item.certified_amount||0)*sign
                  const diff = certified - claimed
                  return (
                    <tr key={item.id} style={{ background:idx%2===0?C.surface:C.surfaceAlt, borderBottom:`1px solid ${C.border}` }}>
                      <td style={{ padding:'8px 10px', textAlign:'center' }} onClick={e=>e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(item.id)} onChange={e=>toggleSelect(item.id,e)} style={{ cursor:'pointer', accentColor: C.blue }}/>
                      </td>
                      <td style={{ padding: S.cellPad, fontFamily:'monospace', fontSize: F.caption, color: C.sub }}>
                        {item.ref||'—'}
                        {item.linked_ref && item.linked_ref!==item.ref && <div style={{ fontSize:10, color: C.blue }}>🔗 {item.linked_ref}</div>}
                      </td>
                      <td style={{ padding: S.cellPad }}>
                        <span style={{ display:'inline-flex', alignItems:'center', padding:'2px 8px', borderRadius: R.tag, fontSize: F.caption, fontWeight:600, background: typeInfo?.color+'18', color: typeInfo?.color }}>
                          {typeInfo?.label||item.item_type}
                        </span>
                      </td>
                      <td style={{ padding: S.cellPad, maxWidth:280 }}>
                        <div style={{ color: C.ink, fontWeight:500, lineHeight:1.4 }}>{item.description}</div>
                        {item.notes && <div style={{ fontSize: F.caption, color: C.sub, marginTop:2 }}>{item.notes}</div>}
                      </td>
                      <td style={{ padding: S.cellPad, fontSize: F.caption, color: C.sub, fontFamily:'monospace' }}>{item.fidic_clause||'—'}</td>
                      <td style={{ padding: S.cellPad, textAlign:'right', fontFamily:'monospace', fontSize: F.body, color: claimed<0?C.red:C.ink }}>{claimed<0?'('+fmt0(Math.abs(claimed))+')':fmt0(claimed)}</td>
                      <td style={{ padding: S.cellPad, textAlign:'right', fontFamily:'monospace', fontSize: F.body, fontWeight:600, color: certified<0?C.red:C.green }}>{certified<0?'('+fmt0(Math.abs(certified))+')':fmt0(certified)}</td>
                      <td style={{ padding: S.cellPad, textAlign:'right', fontFamily:'monospace', fontSize: F.caption, color: diff>0?C.green:diff<0?C.red:C.sub }}>
                        {diff===0?'—':diff>0?'+'+fmt0(diff):'('+fmt0(Math.abs(diff))+')'}
                      </td>
                      <td style={{ padding: S.cellPad, textAlign:'center' }}><span style={badge(item.status)}>{item.status?.charAt(0).toUpperCase()+item.status?.slice(1)}</span></td>
                      <td style={{ padding: S.cellPad, fontSize: F.caption, color: C.sub }}>
                        <span style={{ padding:'1px 6px', borderRadius: R.tag, fontSize:10, background: item.source==='manual'?C.surfaceAlt:item.source==='change_register'?'#DBEAFE':item.source==='eot_register'?'#EDE9FE':'#F0FDF4', color: item.source==='manual'?C.sub:item.source==='change_register'?'#1D4ED8':item.source==='eot_register'?'#7C3AED':C.green }}>
                          {item.source||'manual'}
                        </span>
                      </td>
                      <td style={{ padding:'8px 10px' }}>
                        <div style={{ display:'flex', gap:4 }}>
                          {can('FinalAccount.Edit') && (
                            <button onClick={()=>openEdit(item)} style={{ background:'none', border:'none', cursor:'pointer', color: C.sub, padding:4, borderRadius: R.tag }}>
                              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M11 2l3 3-9 9H2v-3L11 2z"/></svg>
                            </button>
                          )}
                          {can('FinalAccount.Delete') && (
                            <button onClick={()=>deleteItem(item.id)} style={{ background:'none', border:'none', cursor:'pointer', color: C.red, padding:4, borderRadius: R.tag }}>
                              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><polyline points="3,4 13,4"/><path d="M5 4V2h6v2"/><path d="M4 4l1 10h6l1-10"/></svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              {/* Totals row */}
              {filtered.length>0 && (
                <tfoot>
                  <tr style={{ background:'#F9FAFB', borderTop:`2px solid ${C.border2}` }}>
                    <td colSpan={5} style={{ padding: S.cellPad, fontWeight:700, fontSize: F.body, color: C.ink }}>Total ({filtered.length} items)</td>
                    <td style={{ padding: S.cellPad, textAlign:'right', fontFamily:'monospace', fontWeight:700, fontSize: F.body, color: C.ink }}>{fmt0(filtered.reduce((s,i)=>{ const t=ITEM_TYPES.find(t=>t.value===i.item_type); return s+(i.claimed_amount||0)*(t?.sign||1) },0))}</td>
                    <td style={{ padding: S.cellPad, textAlign:'right', fontFamily:'monospace', fontWeight:700, fontSize: F.body, color: C.green }}>{fmt0(filtered.reduce((s,i)=>{ const t=ITEM_TYPES.find(t=>t.value===i.item_type); return s+(i.certified_amount||0)*(t?.sign||1) },0))}</td>
                    <td colSpan={4}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* ── Modal */}
      {showModal && (
        <div style={{ position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(15,16,23,.45)' }}>
          <div style={{ background: C.surface, borderRadius: R.card, width:'100%', maxWidth:640, maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,.15)', margin:'0 16px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', borderBottom:`1px solid ${C.border}` }}>
              <div>
                <h2 style={{ margin:0, fontSize: F.title, fontWeight:700, color: C.ink }}>{editItem?'Edit Item':'Add Final Account Item'}</h2>
                <p style={{ margin:'2px 0 0', fontSize: F.caption, color: C.sub }}>Claimed vs Certified amounts</p>
              </div>
              <button onClick={()=>{ setShowModal(false); setEditItem(null); setForm(emptyForm) }} style={{ background:'none', border:'none', cursor:'pointer', color: C.sub, padding:4 }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="3" y1="3" x2="13" y2="13"/><line x1="13" y1="3" x2="3" y2="13"/></svg>
              </button>
            </div>
            <div style={{ padding:'16px 20px', overflowY:'auto', display:'flex', flexDirection:'column', gap:14 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Item Type *</label><select value={form.item_type} onChange={e=>setForm(f=>({...f,item_type:e.target.value}))} style={{ ...input, background: C.surface }}>{ITEM_TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
                <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Ref</label><input value={form.ref} onChange={e=>setForm(f=>({...f,ref:e.target.value}))} placeholder="VO-001" style={input}/></div>
                <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Status</label><select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))} style={{ ...input, background: C.surface }}>{STATUSES.map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}</select></div>
              </div>
              <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Description *</label><textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} rows={2} style={{ ...input, resize:'none' }}/></div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Claimed Amount (SAR)</label>
                  <input type="number" value={form.claimed_amount} onChange={e=>setForm(f=>({...f,claimed_amount:e.target.value}))} placeholder="0" style={input}/>
                  <p style={{ fontSize: F.caption, color: C.sub, margin:'4px 0 0' }}>Use negative for omissions / deductions</p>
                </div>
                <div>
                  <label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Certified Amount (SAR)</label>
                  <input type="number" value={form.certified_amount} onChange={e=>setForm(f=>({...f,certified_amount:e.target.value}))} placeholder="0" style={input}/>
                  {form.claimed_amount && form.certified_amount && (
                    <p style={{ fontSize: F.caption, margin:'4px 0 0', color: parseFloat(form.certified_amount)>=parseFloat(form.claimed_amount)?C.green:C.red }}>
                      Diff: {parseFloat(form.certified_amount)-parseFloat(form.claimed_amount)>=0?'+':''}{fmt0(parseFloat(form.certified_amount)-parseFloat(form.claimed_amount))}
                    </p>
                  )}
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>FIDIC Clause</label><input value={form.fidic_clause} onChange={e=>setForm(f=>({...f,fidic_clause:e.target.value}))} placeholder="13.1" style={input}/></div>
                <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Linked Ref (VO / EOT)</label><input value={form.linked_ref} onChange={e=>setForm(f=>({...f,linked_ref:e.target.value}))} placeholder="VO-001" style={input}/></div>
                <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Approved Date</label><input type="date" value={form.approved_date} onChange={e=>setForm(f=>({...f,approved_date:e.target.value}))} style={input}/></div>
              </div>
              <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Notes</label><textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={2} style={{ ...input, resize:'none' }}/></div>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8, padding:'14px 20px', borderTop:`1px solid ${C.border}` }}>
              <button onClick={()=>{ setShowModal(false); setEditItem(null); setForm(emptyForm) }} style={btnSecondary}>Cancel</button>
              <button onClick={saveItem} disabled={saving||!form.description} style={{ ...btnPrimary, opacity:saving||!form.description?0.6:1 }}>
                {saving?'Saving…':(editItem?'Update Item':'Add Item')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
